import { BrowserProvider, Contract, formatUnits, parseUnits, JsonRpcProvider, Interface, type Signer } from 'ethers';
import {
  INFRACOINN_ADDRESSES,
  POLYGON_CHAIN_ID,
  POLYGON_RPC,
  ABI_ERC20_MINIMAL,
  ABI_RWA_FACTORY,
  ABI_RWA,
  ABI_STAKING,
  ABI_GOVERNANCE,
  ABI_MARKETPLACE,
} from './infracoinn-contracts';

// ── Types ────────────────────────────────────────────────────────────────────

export interface WalletState {
  address: string;
  chainId: number;
  isCorrectNetwork: boolean;
}

export interface OMXTokenInfo {
  name: string;
  symbol: string;
  decimals: number;
  totalSupply: string;
  balance?: string; // if wallet connected
}

export interface RWAAsset {
  assetId: number;
  contractAddr: string;
  name: string;
  symbol: string;
  owner: string;
  certified: boolean;
}

export interface StakingInfo {
  totalStaked: string;
  apr: number;
  userStaked?: string;
  pendingRewards?: string;
}

export interface DeployAssetParams {
  name: string;
  symbol: string;
  totalSupply: string;   // in human-readable units
  metadataURI: string;
  ownerAddress: string;
}

export interface DeployResult {
  txHash: string;
  assetId?: number;
  contractAddress?: string;
  polygonscanUrl: string;
}

// ── Provider helpers ──────────────────────────────────────────────────────────

declare global {
  interface Window {
    ethereum?: {
      request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
      on: (event: string, handler: (...args: unknown[]) => void) => void;
      removeListener: (event: string, handler: (...args: unknown[]) => void) => void;
      isMetaMask?: boolean;
    };
  }
}

export function hasMetaMask(): boolean {
  return typeof window !== 'undefined' && Boolean(window.ethereum);
}

async function getBrowserProvider(): Promise<BrowserProvider> {
  if (!window.ethereum) throw new Error('MetaMask no está instalado');
  return new BrowserProvider(window.ethereum);
}

async function getSigner(): Promise<Signer> {
  const provider = await getBrowserProvider();
  await provider.send('eth_requestAccounts', []);
  return provider.getSigner();
}

// ── Network switch ────────────────────────────────────────────────────────────

export async function switchToPolygon(): Promise<void> {
  if (!window.ethereum) throw new Error('MetaMask no está instalado');

  try {
    await window.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: '0x89' }], // 137 in hex
    });
  } catch (err: unknown) {
    // 4902 = chain not added yet
    if ((err as { code?: number }).code === 4902) {
      await window.ethereum.request({
        method: 'wallet_addEthereumChain',
        params: [{
          chainId: '0x89',
          chainName: 'Polygon Mainnet',
          nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
          rpcUrls: [POLYGON_RPC],
          blockExplorerUrls: ['https://polygonscan.com'],
        }],
      });
    } else {
      throw err;
    }
  }
}

// ── Wallet connection ─────────────────────────────────────────────────────────

export async function connectWallet(): Promise<WalletState> {
  const provider = await getBrowserProvider();
  await provider.send('eth_requestAccounts', []);

  const signer = await provider.getSigner();
  const address = await signer.getAddress();
  const network = await provider.getNetwork();
  const chainId = Number(network.chainId);

  if (chainId !== POLYGON_CHAIN_ID) {
    await switchToPolygon();
  }

  return {
    address,
    chainId,
    isCorrectNetwork: chainId === POLYGON_CHAIN_ID,
  };
}

// ── OMX Token info (read-only, no wallet needed) ──────────────────────────────

export async function getOMXTokenInfo(walletAddress?: string): Promise<OMXTokenInfo> {
  
  const provider = new JsonRpcProvider(POLYGON_RPC);
  const contract = new Contract(INFRACOINN_ADDRESSES.OMX_TOKEN, ABI_ERC20_MINIMAL, provider);

  const [name, symbol, decimals, totalSupply] = await Promise.all([
    contract.name(),
    contract.symbol(),
    contract.decimals(),
    contract.totalSupply(),
  ]);

  let balance: string | undefined;
  if (walletAddress) {
    const rawBalance = await contract.balanceOf(walletAddress);
    balance = formatUnits(rawBalance, decimals);
  }

  return {
    name,
    symbol,
    decimals: Number(decimals),
    totalSupply: formatUnits(totalSupply, decimals),
    balance,
  };
}

// ── RWA Assets (read-only) ────────────────────────────────────────────────────

export async function getRWAAssets(): Promise<RWAAsset[]> {
  try {
    
    const provider = new JsonRpcProvider(POLYGON_RPC);
    const factory = new Contract(INFRACOINN_ADDRESSES.RWA_FACTORY, ABI_RWA_FACTORY, provider);

    const assets = await factory.getAllAssets();
    return assets.map((a: { contractAddr: string; name: string; symbol: string; owner: string; certified: boolean }, idx: number) => ({
      assetId: idx,
      contractAddr: a.contractAddr,
      name: a.name,
      symbol: a.symbol,
      owner: a.owner,
      certified: a.certified,
    }));
  } catch {
    // Factory may not have getAllAssets — return empty gracefully
    return [];
  }
}

// ── Staking info (read-only) ──────────────────────────────────────────────────

export async function getStakingInfo(walletAddress?: string): Promise<StakingInfo> {
  
  const provider = new JsonRpcProvider(POLYGON_RPC);
  const staking = new Contract(INFRACOINN_ADDRESSES.STAKING, ABI_STAKING, provider);

  const [totalStaked, apr] = await Promise.all([
    staking.totalStaked().catch(() => BigInt(0)),
    staking.apr().catch(() => BigInt(0)),
  ]);

  let userStaked: string | undefined;
  let pendingRewards: string | undefined;

  if (walletAddress) {
    const [stakedRaw, rewardsRaw] = await Promise.all([
      staking.stakedBalance(walletAddress).catch(() => BigInt(0)),
      staking.pendingRewards(walletAddress).catch(() => BigInt(0)),
    ]);
    userStaked = formatUnits(stakedRaw, 18);
    pendingRewards = formatUnits(rewardsRaw, 18);
  }

  return {
    totalStaked: formatUnits(totalStaked, 18),
    apr: Number(apr) / 100, // assume basis points
    userStaked,
    pendingRewards,
  };
}

// ── Deploy new RWA asset via Factory (requires MetaMask) ──────────────────────

export async function deployRWAAsset(params: DeployAssetParams): Promise<DeployResult> {
  const signer = await getSigner();
  const factory = new Contract(INFRACOINN_ADDRESSES.RWA_FACTORY, ABI_RWA_FACTORY, signer);

  const supplyBN = parseUnits(params.totalSupply, 18);

  // Try createERC20Asset first, fallback to createAsset
  let tx: { hash: string; wait: () => Promise<{ logs: { topics: string[] }[] }> };
  let contractAddress: string | undefined;
  let assetId: number | undefined;

  try {
    tx = await factory.createERC20Asset(
      params.name,
      params.symbol,
      supplyBN,
      params.ownerAddress,
    );
  } catch {
    tx = await factory.createAsset(
      params.name,
      params.symbol,
      supplyBN,
      params.metadataURI || '',
      params.ownerAddress,
    );
  }

  const receipt = await tx.wait();

  // Parse AssetCreated event to get assetId and contractAddress
  if (receipt?.logs) {
    for (const log of receipt.logs) {
      if (log.topics[0]) {
        // AssetCreated(uint256 assetId, address contractAddr, address owner, ...)
        try {
          const iface = new Interface(ABI_RWA_FACTORY);
          const parsed = iface.parseLog(log as { topics: string[]; data: string });
          if (parsed?.name === 'AssetCreated') {
            assetId = Number(parsed.args[0]);
            contractAddress = parsed.args[1] as string;
          }
        } catch {
          // ignore parse errors on unrelated logs
        }
      }
    }
  }

  return {
    txHash: tx.hash,
    assetId,
    contractAddress,
    polygonscanUrl: `https://polygonscan.com/tx/${tx.hash}`,
  };
}

// ── Stake OMX ─────────────────────────────────────────────────────────────────

export async function stakeOMX(amountHuman: string): Promise<string> {
  const signer = await getSigner();
  const omx = new Contract(INFRACOINN_ADDRESSES.OMX_TOKEN, ABI_ERC20_MINIMAL, signer);
  const staking = new Contract(INFRACOINN_ADDRESSES.STAKING, ABI_STAKING, signer);

  const amount = parseUnits(amountHuman, 18);

  // Approve staking contract first
  const approveTx = await omx.approve(INFRACOINN_ADDRESSES.STAKING, amount);
  await approveTx.wait();

  const stakeTx = await staking.stake(amount);
  await stakeTx.wait();

  return stakeTx.hash;
}

// ── Claim staking rewards ─────────────────────────────────────────────────────

export async function claimStakingRewards(): Promise<string> {
  const signer = await getSigner();
  const staking = new Contract(INFRACOINN_ADDRESSES.STAKING, ABI_STAKING, signer);
  const tx = await staking.claimRewards();
  await tx.wait();
  return tx.hash;
}

// ── Governance: get proposal state label ─────────────────────────────────────

export const PROPOSAL_STATES = [
  'Pending', 'Active', 'Canceled', 'Defeated', 'Succeeded', 'Queued', 'Expired', 'Executed',
] as const;

// ── PolygonScan URLs ──────────────────────────────────────────────────────────

export const polygonscanAddress = (addr: string) => `https://polygonscan.com/address/${addr}`;
export const polygonscanTx      = (hash: string) => `https://polygonscan.com/tx/${hash}`;
export const polygonscanToken   = (addr: string) => `https://polygonscan.com/token/${addr}`;
